import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import FormLogo from "@/components/FormLogo";
import EmergencyToggle from "@/components/EmergencyToggle";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Section, Field, RadioGroup, CheckboxGroup, FileField, TopNotice,
  ApplicantSpocSection, RequestedBySection, SelfSkipSection, makeUploader,
  isHodDropdownOptional, stripSelfSkipTransients, ResubmitFromSection
} from "@/components/FormShell";

const BOOKING_TO = ["Students", "Staff", "HOD", "Managers", "Parents", "Guest",
  "Senior Management", "New Parents Prospects"];
const VEHICLES = ["Flight", "Train", "Bus", "Taxi"];

export default function NewTravel() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);
  const [form, setForm] = useState({
    staffName: user?.name || "", staffEmail: user?.email || "", staffContact: "",
    mainDept: "", subDept: "", hodApproval: "", is_hod_self: undefined, is_admin1_self: undefined, admin1_self_cost: "", admin1_self_remark: "",
    // III
    purposeOfTravel: "", peopleCount: "",
    travelBookingTo: [], travelVehicle: "", vehicleReason: "",
    // IV — Phase 11 Frequent Flyer (optional)
    is_frequent_flyer: "", frequent_flyer_number: "", airline_number: "",
    // V
    country: "", state: "", city: "",
    // VI
    bookingDate: "", bookingTime: "",
    vehicleNameSpecific: "", preferredSeating: "",
    departureDate: "", departureTime: "",
    arrivalDate: "", arrivalTime: "",
    // VI
    includingFood: "No", vegOrMulti: "",
    // Phase 13.2 — Return Travel Tickets (all optional; if any is filled,
    // returnDate becomes required at submit time).
    return_travel: {
      returnDate: "", returnTime: "", returnVehicle: "", returnSeat: "",
      returnCityFrom: "", returnCityTo: "", returnRemarks: "",
    },
    is_emergency: false, emergency_reason: "",
  });

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
  }, []);

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onUpload = makeUploader(api, setAttachments, setUploading, toast, formatApiError);

  const validate = () => {
    if (!(form.is_hod_self === true || form.is_admin1_self === true) && !String(form.hodApproval || "").trim()) {
      toast.error("Please select an HOD or declare Yes on Are you a HOD? / Admin-1?");
      return false;
    }
    for (const k of ["staffName", "staffEmail", "staffContact", "mainDept", "subDept",
                      "purposeOfTravel", "peopleCount", "travelVehicle",
                     "country", "state", "city", "departureDate", "arrivalDate"]) {
      if (!String(form[k] || "").trim()) { toast.error(`Please fill: ${k}`); return false; }
    }
    if (form.travelBookingTo.length === 0) { toast.error("Select at least one category."); return false; }
    if (form.includingFood === "Yes" && !form.vegOrMulti) { toast.error("Select veg or multi-cuisine."); return false; }
    // Phase 13.2 — Return trip: if any return field filled, returnDate must be set.
    const rt = form.return_travel || {};
    const anyReturn = ["returnTime", "returnVehicle", "returnSeat",
                        "returnCityFrom", "returnCityTo", "returnRemarks"]
                       .some((k) => String(rt[k] || "").trim());
    if (anyReturn && !String(rt.returnDate || "").trim()) {
      toast.error("Return date is required when other return fields are filled.");
      return false;
    }
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).");
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/requisitions/travel", {
        form_type: "travel", parent_ref_code: parentRefCode || undefined, form_data: { ...stripSelfSkipTransients(form), attachments },
      });
      toast.success(`Submitted — ${data.ref_code}`);
      nav(`/requisitions/${data.ref_code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <div className="max-w-5xl mx-auto relative">
        <FormLogo />
        <div className="mb-4 text-[11px] uppercase tracking-widest text-[#1c4a4a] font-semibold">
          VGWS-TVL-2026 · Travel Tickets
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight">
          Travel Tickets Booking Requisition Form
        </h1>
        <p className="text-sm text-[#4a524c] mt-1">
          Vishwashanti Gurukul World School · Rajbaug, Loni Kalbhor
        </p>

        <TopNotice>
          <div className="font-serif font-semibold mb-1 flex items-center gap-2">
            <AlertCircle size={16} /> Important
          </div>
          <p>Submit this form ideally at least <strong>20 days</strong> prior to the requirement.</p>
        </TopNotice>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({ ...f, is_emergency: v, emergency_reason: v ? f.emergency_reason : "" }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setForm((f) => ({ ...f, emergency_reason: v }))}
        />

        <form className="mt-6 space-y-6" onSubmit={onSubmit} data-testid="travel-form">
          <ResubmitFromSection
            isResubmit={isResubmit}
            onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
            parentRefCode={parentRefCode}
            onSelectHistory={(fd, ref) => {
              setForm((f) => ({ ...f, ...fd,
                staffName: f.staffName || fd.staffName || "",
                staffEmail: f.staffEmail || fd.staffEmail || "",
              }));
              setParentRefCode(ref);
            }}
          />

          <ApplicantSpocSection form={form} setField={setField} />
          <SelfSkipSection form={form} setField={setField} userRole={user?.role} />
          <RequestedBySection form={form} setField={setField} depts={depts} hods={hods} subDepts={subDepts} hodOptional={isHodDropdownOptional(form)} />

          <Section numeral="III" title="Travel details">
            <Field label="Purpose of travel — describe in detail" required>
              <textarea className="input-vgws min-h-[70px]" required value={form.purposeOfTravel}
                        onChange={(e) => setField("purposeOfTravel", e.target.value)}
                        data-testid="field-purposeOfTravel" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="How many people tickets" required>
                <input type="number" min="1" className="input-vgws" value={form.peopleCount}
                       onChange={(e) => setField("peopleCount", e.target.value)}
                       data-testid="field-peopleCount" />
              </Field>
              <Field label="Travel booking to whom" required hint="Select at least one">
                <CheckboxGroup value={form.travelBookingTo}
                               onChange={(v) => setField("travelBookingTo", v)}
                               options={BOOKING_TO} testid="field-travelBookingTo" />
              </Field>
            </div>
            <Field label="Travel vehicle" required>
              <RadioGroup name="travelVehicle" value={form.travelVehicle}
                          onChange={(v) => setField("travelVehicle", v)}
                          options={VEHICLES} testid="field-travelVehicle" />
            </Field>
            <Field label="Reason for specific travel vehicle — describe in detail" required>
              <textarea className="input-vgws min-h-[60px]" value={form.vehicleReason}
                        onChange={(e) => setField("vehicleReason", e.target.value)}
                        data-testid="field-vehicleReason" />
            </Field>
            <FileField label="Attach identity proof of traveller(s)" fieldName="idProof"
                       accept=".pdf,.jpg,.jpeg,.png"
                       attachments={attachments} onChange={(e) => onUpload("idProof", e)}
                       disabled={uploading} testid="attach-idProof" optional />
          </Section>

          {/* Phase 11 — Frequent Flyer (optional) */}
          <Section numeral="IV" title="Frequent Flyer (optional)">
            <Field label="Is traveller a Frequent Flyer?">
              <div className="flex gap-2" data-testid="field-is_frequent_flyer">
                {["Yes", "No"].map((v) => (
                  <label key={v}
                          className={`px-4 py-2 rounded-md border cursor-pointer text-sm font-medium transition-all ${
                            form.is_frequent_flyer === v
                              ? "bg-[#7a1f2b] text-white border-[#5a1520]"
                              : "bg-white text-[#7a1f2b] border-[#e2d7c3] hover:border-[#7a1f2b]"
                          }`}>
                    <input type="radio" className="hidden" name="is_frequent_flyer" value={v}
                            checked={form.is_frequent_flyer === v}
                            onChange={() => setField("is_frequent_flyer", v)} />
                    {v}
                  </label>
                ))}
              </div>
            </Field>
            {form.is_frequent_flyer === "Yes" && (
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Frequent Flyer Number">
                  <input className="input-vgws" value={form.frequent_flyer_number || ""}
                          onChange={(e) => setField("frequent_flyer_number", e.target.value)}
                          data-testid="field-frequent_flyer_number" />
                </Field>
                <Field label="Airlines Name / Number">
                  <input className="input-vgws" value={form.airline_number || ""}
                          onChange={(e) => setField("airline_number", e.target.value)}
                          data-testid="field-airline_number" />
                </Field>
              </div>
            )}
          </Section>

          <Section numeral="V" title="Location">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Country" required>
                <input className="input-vgws" value={form.country}
                       onChange={(e) => setField("country", e.target.value)} data-testid="field-country" />
              </Field>
              <Field label="State" required>
                <input className="input-vgws" value={form.state}
                       onChange={(e) => setField("state", e.target.value)} data-testid="field-state" />
              </Field>
              <Field label="City" required>
                <input className="input-vgws" value={form.city}
                       onChange={(e) => setField("city", e.target.value)} data-testid="field-city" />
              </Field>
            </div>
          </Section>

          <Section numeral="VI" title="Booking & schedule">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Travel booking date" required>
                <input type="date" className="input-vgws" value={form.bookingDate}
                       onChange={(e) => setField("bookingDate", e.target.value)}
                       data-testid="field-bookingDate" />
              </Field>
              <Field label="Travel booking time" required>
                <input type="time" className="input-vgws" value={form.bookingTime}
                       onChange={(e) => setField("bookingTime", e.target.value)}
                       data-testid="field-bookingTime" />
              </Field>
              <Field label="Travel vehicle name (specific)">
                <input className="input-vgws" value={form.vehicleNameSpecific}
                       onChange={(e) => setField("vehicleNameSpecific", e.target.value)}
                       data-testid="field-vehicleNameSpecific" />
              </Field>
              <Field label="Preferred seating option">
                <input className="input-vgws" value={form.preferredSeating}
                       onChange={(e) => setField("preferredSeating", e.target.value)}
                       data-testid="field-preferredSeating" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sheet-plain p-4">
                <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold mb-3">Departure</div>
                <div className="grid gap-3">
                  <Field label="Departure date" required>
                    <input type="date" className="input-vgws" value={form.departureDate}
                           onChange={(e) => setField("departureDate", e.target.value)}
                           data-testid="field-departureDate" />
                  </Field>
                  <Field label="Departure tentative time" required>
                    <input type="time" className="input-vgws" value={form.departureTime}
                           onChange={(e) => setField("departureTime", e.target.value)}
                           data-testid="field-departureTime" />
                  </Field>
                </div>
              </div>
              <div className="sheet-plain p-4">
                <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold mb-3">Arrival</div>
                <div className="grid gap-3">
                  <Field label="Arrival date" required>
                    <input type="date" className="input-vgws" value={form.arrivalDate}
                           onChange={(e) => setField("arrivalDate", e.target.value)}
                           data-testid="field-arrivalDate" />
                  </Field>
                  <Field label="Arrival tentative time" required>
                    <input type="time" className="input-vgws" value={form.arrivalTime}
                           onChange={(e) => setField("arrivalTime", e.target.value)}
                           data-testid="field-arrivalTime" />
                  </Field>
                </div>
              </div>
            </div>
          </Section>

          <Section numeral="VII" title="Return Travel Tickets"
                   description="Fill any of these if a return leg is booked. Return date is required only when any other return field is filled.">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Return date">
                <input type="date" className="input-vgws"
                       value={form.return_travel.returnDate}
                       onChange={(e) => setField("return_travel",
                          { ...form.return_travel, returnDate: e.target.value })}
                       data-testid="field-returnDate" />
              </Field>
              <Field label="Tentative return time">
                <input type="time" className="input-vgws"
                       value={form.return_travel.returnTime}
                       onChange={(e) => setField("return_travel",
                          { ...form.return_travel, returnTime: e.target.value })}
                       data-testid="field-returnTime" />
              </Field>
              <Field label="Return travel vehicle name">
                <input className="input-vgws"
                       placeholder="Flight IX-123 / Train 12345"
                       value={form.return_travel.returnVehicle}
                       onChange={(e) => setField("return_travel",
                          { ...form.return_travel, returnVehicle: e.target.value })}
                       data-testid="field-returnVehicle" />
              </Field>
              <Field label="Preferred seat">
                <input className="input-vgws"
                       placeholder="Aisle / Window / 12A"
                       value={form.return_travel.returnSeat}
                       onChange={(e) => setField("return_travel",
                          { ...form.return_travel, returnSeat: e.target.value })}
                       data-testid="field-returnSeat" />
              </Field>
              <Field label="Return — city from">
                <input className="input-vgws"
                       value={form.return_travel.returnCityFrom}
                       onChange={(e) => setField("return_travel",
                          { ...form.return_travel, returnCityFrom: e.target.value })}
                       data-testid="field-returnCityFrom" />
              </Field>
              <Field label="Return — city to">
                <input className="input-vgws"
                       value={form.return_travel.returnCityTo}
                       onChange={(e) => setField("return_travel",
                          { ...form.return_travel, returnCityTo: e.target.value })}
                       data-testid="field-returnCityTo" />
              </Field>
            </div>
            <Field label="Additional return details">
              <textarea className="input-vgws min-h-[70px]"
                        value={form.return_travel.returnRemarks}
                        onChange={(e) => setField("return_travel",
                          { ...form.return_travel, returnRemarks: e.target.value })}
                        data-testid="field-returnRemarks" />
            </Field>
          </Section>

          <Section numeral="VIII" title="Food preference">
            <Field label="Including food?" required>
              <RadioGroup name="includingFood" value={form.includingFood}
                          onChange={(v) => setField("includingFood", v)}
                          options={["Yes", "No"]} testid="field-includingFood" />
            </Field>
            {form.includingFood === "Yes" && (
              <Field label="Veg or multi-cuisine" required>
                <RadioGroup name="vegOrMulti" value={form.vegOrMulti}
                            onChange={(v) => setField("vegOrMulti", v)}
                            options={["Veg", "Multi Cuisine"]} testid="field-vegOrMulti" />
              </Field>
            )}
          </Section>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-[#6e7770] max-w-md">
              Fields marked <span className="text-[#b3261e]">*</span> are required.
            </p>
            <button type="submit" disabled={submitting} className="btn-vgws" data-testid="submit-travel-button">
              <Send size={16} /> {submitting ? "Submitting…" : "Submit requisition"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
